import React, { useState } from 'react'
import { useCookies } from 'react-cookie'
import { useForm } from 'react-hook-form'
import { SubmitErrorHandler, SubmitHandler } from 'react-hook-form'
import { Button, Checkbox, InputAdornment, TextField, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import axios from 'axios'
import Link from 'next/link'
import { useRouter } from 'next/router'

import ClosedEyeSvg from '#/assets/svg/closed-eye.svg?react'
import OpenedEyeSvg from '#/assets/svg/opened-eye.svg?react'
import { PasswordOptions } from '#/features/register-by-email/lib/constants'
import { IEmailForms } from '#/features/register-by-email/model/types'
import { emailOptions } from '#/shared'
import { InputStyleDark } from '#/shared'
import { getApiUrl } from '#/shared/lib/constants'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'
import { useDeviceType } from '#/shared/lib/hooks/use-device-type'

interface IRegisterEmailFormProps {
	successLogin: () => void
}

export const RegisterEmailForm: React.FC<IRegisterEmailFormProps> = ({ successLogin }) => {
	const { register, handleSubmit, reset, watch } = useForm()

	const { desktop } = useDeviceType()
	const inputStyles = desktop
		? InputStyleDark
		: {
				...InputStyleDark,
				'& .MuiOutlinedInput-input': { padding: '10px 12px' },
			}

	const { showMessage } = useShowDataStore()

	const { push } = useRouter()

	const [loading, setLoading] = React.useState(false)

	const [cookie] = useCookies()
	const [passwordShowed, showPassword] = React.useState(false)
	const [subscribedToEmails, setSubscribedToEmails] = React.useState(false)

	const makeNewAccount: SubmitHandler<any> = async (data) => {
		setLoading(true)

		const req_data: {
			email: any
			password: any
			utm_source: any
			utm_medium: any
			utm_campaign: any
			utm_term: any
			utm_content: any
			referer?: string | null
			subscribed_to_emails?: boolean
		} = {
			email: data.email,
			password: data.password1,
			utm_source: cookie.utm_source,
			utm_medium: cookie.utm_medium,
			utm_campaign: cookie.utm_campaign,
			utm_term: cookie.utm_term,
			utm_content: cookie.utm_content,
		}

		if (localStorage.getItem('referral')) {
			req_data.referer = localStorage.getItem('referral')
		}

		if (subscribedToEmails) {
			req_data.subscribed_to_emails = true
		}

		const { status, data: result } = await axios.post(getApiUrl() + '/auth/register', req_data)

		if (status !== 201) {
			setLoading(false)
			showMessage(result.detail)
			return
		}

		setLoading(false)
		reset()
		successLogin()
		setTimeout(() => push('/login'), 7000)
	}

	const checkError: SubmitErrorHandler<IEmailForms> = (data) => {
		showMessage(Object.values(data)[0].message || 'Неверные данные')
	}

	const handleInputChangeTrim = (event: any) => {
		event.target.value = event.target.value.trim()
	}

	const handleKeyDown = (event: any) => {
		if (event.key === 'Enter') {
			handleSubmit(makeNewAccount)()
		}
	}

	return (
		<form onKeyDown={handleKeyDown}>
			<Typography
				variant='body2'
				sx={{
					color: '#A6A5A5',
					lineHeight: '19.6px',
					fontSize: '14px',
					fontWeight: '550',
					paddingTop: 1,
					width: '100%',
					marginBottom: 1.5,
				}}
			>
				Email
			</Typography>
			<TextField
				fullWidth
				sx={{ ...inputStyles }}
				{...register('email', emailOptions)}
				onChange={handleInputChangeTrim}
			/>

			<Typography
				variant='body2'
				sx={{
					color: '#A6A5A5',
					lineHeight: '19.6px',
					fontSize: '14px',
					fontWeight: '550',
					paddingTop: 1,
					width: '100%',
					marginBottom: 1.5,
				}}
			>
				Пароль
			</Typography>

			<TextField
				type={passwordShowed ? 'text' : 'password'}
				fullWidth
				InputProps={{
					endAdornment: (
						<InputAdornment position='start'>
							<div
								onClick={() => showPassword((x) => !x)}
								style={{
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
								}}
							>
								{passwordShowed ? (
									<ClosedEyeSvg width={24} height={24} color='grey' />
								) : (
									<OpenedEyeSvg width={24} height={24} color='grey' />
								)}
							</div>
						</InputAdornment>
					),
				}}
				sx={{ ...inputStyles }}
				{...register('password1', {
					...PasswordOptions,
				})}
			/>

			<Typography
				variant='body2'
				sx={{
					color: '#A6A5A5',
					lineHeight: '19.6px',
					fontSize: '14px',
					fontWeight: '550',
					paddingTop: 1,
					width: '100%',
					marginBottom: 1.5,
				}}
			>
				Подтвердите пароль
			</Typography>

			<TextField
				type={passwordShowed ? 'text' : 'password'}
				fullWidth
				sx={{ ...inputStyles }}
				{...register('password2', {
					...PasswordOptions,
					validate: (val: string) => {
						if (watch('password1') != val) {
							return 'Пароли не совпадают'
						}
					},
				})}
			/>

			<Box display='flex' alignItems='flex-start' sx={{ marginTop: 2 }}>
				<Checkbox
					checked={subscribedToEmails}
					onChange={(e) => setSubscribedToEmails(e.target.checked)}
					size='medium'
					sx={{
						color: '#7F7DF3',
						paddingTop: 0,
						'&.Mui-checked': { color: '#7F7DF3' },
					}}
				/>
				<Typography sx={{ color: '#868686', fontSize: '14px' }}>
					Хочу получать полезные материалы про ИИ и маркетинговые сообщения на email
				</Typography>
			</Box>

			<Box sx={{ width: '100%' }}>
				{loading ? (
					<Box
						style={{
							margin: '25px auto',
							display: 'flex',
							justifyContent: 'center',
						}}
					>
						<CircularProgress size={30} thickness={5} sx={{ color: '#7F7DF3' }} />
					</Box>
				) : (
					<Button
						variant='contained'
						style={{
							marginTop: '25px',
						}}
						sx={{
							backgroundColor: '#7F7DF3',
							color: '#FFFFFF',
							lineHeight: '20px',
							fontSize: '16px',
							fontWeight: '600px',
							borderRadius: '13px',
							textTransform: 'none',
							width: desktop ? '177px' : '100%',
							height: 50,
							'&:hover': {
								backgroundColor: '#7F7DF3',
								opacity: [0.9, 0.8, 0.7],
							},
						}}
						onClick={handleSubmit(makeNewAccount, checkError)}
					>
						Создать аккаунт
					</Button>
				)}
			</Box>
			<p style={{ marginTop: '15px', fontSize: 15, color: '#868686' }}>
				<span>Регистрируясь, вы соглашаетесь с условиями&nbsp;</span>
				<Link style={{ color: 'var(--air-color)' }} href='https://air.fail/personal'>
					Политики обработки персональных данных&nbsp;
				</Link>
				<span>и&nbsp;</span>
				<Link style={{ color: 'var(--air-color)' }} href='https://air.fail/offer'>
					Публичной офертой
				</Link>
			</p>
			<Box sx={{ width: '100%', textAlign: 'center', marginTop: '35px' }}>
				<Typography
					sx={{
						color: '#A7A8BB',
						fontSize: '16px',
					}}
				>
					Есть аккаунт?{' '}
					<Link
						href='/login'
						style={{
							textDecoration: 'none',
							color: '#7F7DF3',
						}}
					>
						Войти
					</Link>
				</Typography>
			</Box>
		</form>
	)
}
